// Tjenester og priser (/tjenester), i startsidens stil (planer/hygraph-plan.md): verkstedets
// tjenester med pris og tid (også det som bare finnes i butikken, som trykk), gruppene på
// nettsiden, kapasiteten per butikk og stengte dager. Erstatter redigeringen i Hygraph og den
// gamle fastprislisten («Fastpris arbeid»), som er slått sammen med katalogen.
// Alle ansatte kan se; bare leder og mekaniker kan endre (backenden sjekker det samme).
import React, { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Tilbake, Sidetopp, Valgknapper, feltKlasse, knappPrimar, knappSekundar } from '../../ny-kundesamhandling/Skjema';
import Ikon from '../../ny-kundesamhandling/Ikon';
import { useKanEndrePriser } from '../../employeeAccess';
import { KATEGORI_NAVN, BOOKING_NAVN, kr, min, beggeSiderPris, lagreIKatalog, useKatalogAdmin, sistEndret } from './katalogFelles';
import { Melding } from './Velgere';
import Grupper from './Grupper';
import Kapasitet from './Kapasitet';
import Overstyringer from './Overstyringer';

const FANER = [
  { verdi: 'tjenester', tittel: 'Tjenester' },
  { verdi: 'grupper', tittel: 'Grupper' },
  { verdi: 'kapasitet', tittel: 'Kapasitet' },
  { verdi: 'stengt', tittel: 'Stengte dager og endringer' },
];

const KATEGORIVALG = [
  { verdi: '', tittel: 'Alle' },
  { verdi: 'sykkelservice', tittel: 'Sykkel' },
  { verdi: 'skiservice', tittel: 'Ski' },
  { verdi: 'skoyteslip', tittel: 'Skøyter' },
  { verdi: 'tekstiltrykk', tittel: 'Trykk' },
];

// Pris og tid kan endres rett i lista; resten på tjenestens egen side.
function TjenesteRad({ tjeneste, grupper, kanEndre, onLagret }) {
  const [pris, setPris] = useState(tjeneste.pris ?? '');
  const [tid, setTid] = useState(tjeneste.tidsbruk ?? '');
  const [status, setStatus] = useState({ type: '', tekst: '' });
  const endret = String(pris ?? '') !== String(tjeneste.pris ?? '') || String(tid ?? '') !== String(tjeneste.tidsbruk ?? '');
  const inaktiv = tjeneste.aktiv === false;
  const id = `t-${tjeneste._id}`;

  useEffect(() => {
    setPris(tjeneste.pris ?? '');
    setTid(tjeneste.tidsbruk ?? '');
  }, [tjeneste.pris, tjeneste.tidsbruk]);

  async function lagre(e) {
    e.preventDefault();
    setStatus({ type: 'lagrer', tekst: 'Lagrer …' });
    try {
      await lagreIKatalog('PATCH', `tjenester/${tjeneste._id}`, { pris: pris === '' ? null : pris, tidsbruk: tid === '' ? 0 : tid });
      setStatus({ type: 'ok', tekst: 'Lagret' });
      onLagret();
    } catch (err) {
      setStatus({ type: 'feil', tekst: err.message });
    }
  }

  const tall = (v, sett) => (e) => {
    const t = e.target.value.replace(/\D/g, '');
    sett(t === '' ? '' : Number(t));
  };

  return (
    <li className={`rounded-2xl border-2 bg-white p-3 ${inaktiv ? 'border-dashed border-slate-300' : 'border-slate-200'}`}>
      <div className="flex flex-wrap items-start gap-x-3 gap-y-2">
        <div className="min-w-0 flex-1 basis-64">
          <Link to={`/tjenester/${tjeneste._id}`} className="font-bold text-indigo-800 underline-offset-2 hover:underline [overflow-wrap:anywhere]">
            {tjeneste.tjeneste}
          </Link>
          <div className="mt-1 flex flex-wrap gap-1.5 text-sm">
            <span className="rounded-full bg-slate-100 px-2 py-0.5 text-slate-800">{KATEGORI_NAVN[tjeneste.kategori]}</span>
            {inaktiv && <span className="rounded-full bg-amber-100 px-2 py-0.5 font-semibold text-amber-950">Av</span>}
            {tjeneste.kunIButikk && <span className="rounded-full bg-sky-50 px-2 py-0.5 text-sky-900">Bare i butikken</span>}
            {tjeneste.foranbak && tjeneste.dobbelPris && typeof pris === 'number' && (
              <span className="rounded-full bg-amber-50 px-2 py-0.5 text-amber-950">
                Begge sider: {kr(beggeSiderPris(pris, tjeneste.dobbelPrisRabatt))}
              </span>
            )}
            {grupper.map((g) => <span key={g._id} className="rounded-full bg-indigo-50 px-2 py-0.5 text-indigo-900">{g.tjenestetype}</span>)}
            {(tjeneste.booking || []).map((b) => <span key={b} className="rounded-full bg-emerald-50 px-2 py-0.5 text-emerald-900">{BOOKING_NAVN[b] || b}</span>)}
          </div>
        </div>
        {kanEndre ? (
          <form onSubmit={lagre} className="flex flex-wrap items-end gap-2" aria-label={`Pris og tid for ${tjeneste.tjeneste}`}>
            <div className="w-28">
              <label htmlFor={`${id}-pris`} className="block text-sm font-semibold text-slate-700">Pris (kr)</label>
              <input id={`${id}-pris`} type="text" inputMode="numeric" value={pris} onChange={tall(pris, setPris)} className={`${feltKlasse} py-1.5`} />
            </div>
            <div className="w-24">
              <label htmlFor={`${id}-tid`} className="block text-sm font-semibold text-slate-700">Tid (min)</label>
              <input id={`${id}-tid`} type="text" inputMode="numeric" value={tid} onChange={tall(tid, setTid)} className={`${feltKlasse} py-1.5`} />
            </div>
            {endret && (
              <button type="submit" disabled={status.type === 'lagrer'} className={`${knappPrimar} min-h-[2.25rem] py-1.5 text-base`}>
                <Ikon navn="hake" className="w-5 h-5" /> Lagre
              </button>
            )}
          </form>
        ) : (
          <div className="text-right">
            <div className="text-base font-bold text-slate-900">{kr(tjeneste.pris)}</div>
            <div className="text-sm text-slate-700">{min(tjeneste.tidsbruk)}</div>
          </div>
        )}
      </div>
      {status.tekst && status.type !== 'lagrer' && (
        <p role={status.type === 'feil' ? 'alert' : 'status'} className={`mt-2 text-sm font-semibold ${status.type === 'feil' ? 'text-red-800' : 'text-emerald-800'}`}>
          {status.tekst}
        </p>
      )}
      {status.type === 'lagrer' && <p role="status" className="mt-2 text-sm text-slate-700">Lagrer …</p>}
    </li>
  );
}

function Tjenesteliste({ data, kanEndre, hent }) {
  const [sok, setSok] = useState('');
  const [kategori, setKategori] = useState('');
  const [visAv, setVisAv] = useState(false);
  const grupperPerId = useMemo(() => new Map(data.grupper.map((g) => [g._id, g])), [data.grupper]);

  const filtrert = useMemo(() => {
    const q = sok.trim().toLowerCase();
    return data.tjenester.filter((t) =>
      (!kategori || t.kategori === kategori) &&
      (visAv || t.aktiv !== false) &&
      (!q || t.tjeneste.toLowerCase().includes(q) || t.slug.includes(q)));
  }, [data.tjenester, sok, kategori, visAv]);

  const antallAv = data.tjenester.filter((t) => t.aktiv === false && (!kategori || t.kategori === kategori)).length;

  return (
    <div className="space-y-3">
      <section aria-label="Søk og utvalg" className="space-y-3 rounded-2xl border border-slate-200 bg-slate-50 p-3">
        <div>
          <label htmlFor="tp-sok" className="mb-1 block font-semibold text-slate-800">Søk etter tjeneste</label>
          <div className="relative">
            <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-500"><Ikon navn="sok" className="w-6 h-6" /></span>
            <input id="tp-sok" type="search" autoComplete="off" value={sok} onChange={(e) => setSok(e.target.value)}
              placeholder="F.eks. bremse" aria-describedby="tp-sok-status" className={`${feltKlasse} pl-12`} />
          </div>
          <p id="tp-sok-status" role="status" className="mt-1.5 min-h-[1.25rem] text-sm text-slate-600">
            {`${filtrert.length} ${filtrert.length === 1 ? 'tjeneste' : 'tjenester'}`}
          </p>
        </div>
        <div className="flex flex-wrap items-end gap-x-4 gap-y-2">
          <Valgknapper id="tp-kategori" etikett="Kategori" valg={KATEGORIVALG} verdi={kategori} onVelg={setKategori} />
          {antallAv > 0 && (
            <label className="flex min-h-[2.25rem] items-center gap-2 font-semibold text-slate-800">
              <input type="checkbox" checked={visAv} onChange={(e) => setVisAv(e.target.checked)} className="h-5 w-5 accent-emerald-700" />
              Vis de som er slått av ({antallAv})
            </label>
          )}
        </div>
      </section>

      {filtrert.length === 0 ? (
        <p className="rounded-xl border border-dashed border-slate-300 px-3 py-5 text-slate-700">Ingen tjenester passer.</p>
      ) : (
        <ul aria-label="Tjenester" className="space-y-2">
          {filtrert.map((t) => (
            <TjenesteRad key={t._id} tjeneste={t} kanEndre={kanEndre} onLagret={hent}
              grupper={(t.grupper || []).map((id) => grupperPerId.get(id)).filter(Boolean)} />
          ))}
        </ul>
      )}
    </div>
  );
}

export default function TjenesterOgPriser() {
  const [params, setParams] = useSearchParams();
  const fane = FANER.some((f) => f.verdi === params.get('fane')) ? params.get('fane') : 'tjenester';
  const kanEndre = useKanEndrePriser();
  const { data, feil, hent } = useKatalogAdmin();

  return (
    <div className="mx-auto max-w-7xl space-y-4 pb-10">
      <Tilbake />
      <Sidetopp ikon="lapp" farge="verksted" tittel="Tjenester og priser"
        undertittel="Verkstedets tjenester, kapasitet og stengte dager. Det som lagres her, vises på roykensykkelservice.no med en gang.">
        {kanEndre && fane === 'tjenester' && (
          <Link to="/tjenester/ny" className={knappSekundar}><Ikon navn="pluss" className="w-5 h-5" /> Ny tjeneste</Link>
        )}
      </Sidetopp>

      {!kanEndre && (
        <Melding>Du kan se tjenestene og prisene. Bare leder og mekaniker kan endre dem; velg deg selv øverst i menyen.</Melding>
      )}

      <Valgknapper id="tp-fane" etikett="Vis" valg={FANER} verdi={fane}
        onVelg={(v) => setParams(v === 'tjenester' ? {} : { fane: v }, { replace: true })} />

      {feil && <Melding type="feil">{feil}</Melding>}
      {!data && !feil && <p className="rounded-xl border border-dashed border-slate-300 px-3 py-5 text-slate-700">Henter tjenestekatalogen …</p>}

      {data && fane === 'tjenester' && <Tjenesteliste data={data} kanEndre={kanEndre} hent={hent} />}
      {data && fane === 'grupper' && <Grupper data={data} kanEndre={kanEndre} hent={hent} />}
      {data && fane === 'kapasitet' && <Kapasitet data={data} kanEndre={kanEndre} hent={hent} />}
      {data && fane === 'stengt' && <Overstyringer data={data} kanEndre={kanEndre} hent={hent} />}

      {data && (
        <p className="text-sm text-slate-600">
          {(sistEndret([...data.tjenester, ...data.grupper, ...data.kapasiteter, ...data.overstyringer]
            .filter((p) => p.endringer?.length)
            .sort((a, b) => new Date(a.endringer.at(-1).dato) - new Date(b.endringer.at(-1).dato))
            .at(-1)) || '').replace('Sist endret', 'Sist endret i katalogen')}
        </p>
      )}
    </div>
  );
}
